// Botón "¿Problemas para entrar?" de la pantalla de login.
// Convierte un caso de 5 horas ("mi contraseña no funciona") en uno de 30
// segundos: sondea el backend y le dice al médico, en su idioma, si el problema
// es su clave o la red de su institución.
//
// Colores: solo tokens de theme.js (regla dura del proyecto, cero hex).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Collapse, Stack, Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { checkBackendReachability } from "../../helpers/connectivity-helpers";

const linkBtnSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: 13,
  color: "text.muted",
  "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
};

const panelSx = (ok) => ({
  mt: 1.5,
  p: 1.75,
  borderRadius: "12px",
  textAlign: "left",
  border: "1px solid",
  borderColor: ok
    ? "rgba(var(--color-success-rgb),0.35)"
    : "rgba(var(--color-danger-rgb),0.35)",
  backgroundColor: ok
    ? "rgba(var(--color-success-rgb),0.10)"
    : "rgba(var(--color-danger-rgb),0.10)",
});

const detailSx = {
  mt: 1.25,
  p: 1.25,
  borderRadius: "9px",
  backgroundColor: "var(--bg-input)",
  border: "1px solid rgba(var(--fg-rgb),0.12)",
  color: "text.muted",
  fontFamily:
    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
  fontSize: 11,
  lineHeight: 1.6,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

// Regla de seguridad del consejo, por DEFAULT en vez de por enumeración:
// cuando la sonda dice OK, hablar de la contraseña solo se habilita si el login
// falló por credenciales. Cualquier otro estado (red, timeout, throttle, 5xx,
// almacenamiento, 403, o ningún intento) NUNCA sugiere tocar la clave.
//
// Enumerar los casos "malos" ya falló una vez: se cubrían pending/timeout/server
// pero el panel se auto-abre también con "network", y ahí el médico leía
// "firewall de tu institución" arriba y "pedí un reseteo de clave" abajo. Y es
// un escenario real de prod: un proxy que bloquea el preflight OPTIONS mata el
// POST (manda X-CSRFToken) pero deja pasar la sonda, que es un GET simple.
const hintFor = (report, loginState) => {
  if (!report.ok) return report.hint;
  if (loginState === "credentials") {
    return `${report.hint} Revisa tu correo y tu contraseña, o pide un reseteo de clave.`;
  }
  if (loginState === "pending") {
    return `${report.hint} Seguimos intentando entrar: no cierres esta página.`;
  }
  if (loginState) {
    return `${report.hint} El problema no es tu contraseña: espera unos segundos y vuelve a intentar. Si sigue igual, muéstrale este detalle a soporte.`;
  }
  return report.hint;
};

const ConnectionDiagnostics = ({
  open,
  onToggle,
  loginState = null,
  attempt = 0,
}) => {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [copied, setCopied] = useState(false);
  // Guard en ref, no en estado: el efecto de abajo depende de `open`, y usar
  // `running` como condición lo haría re-disparar en el render intermedio.
  // Este repo ya tuvo un "Maximum update depth" por deps mal puestas.
  const runningRef = useRef(false);
  // Generación de la sonda: si un intento de login nuevo invalida el veredicto
  // mientras una sonda sigue en vuelo, su resultado ya no debe pintarse.
  const genRef = useRef(0);

  // `force` relanza la sonda aunque haya una en vuelo: al bumpear genRef, la
  // anterior queda obsoleta y la nueva pasa a ser la vigente.
  const runCheck = useCallback(async (force = false) => {
    if (runningRef.current && !force) return;
    runningRef.current = true;
    const gen = ++genRef.current;
    setRunning(true);
    setCopied(false);
    setReport(null);
    try {
      const result = await checkBackendReachability();
      if (gen === genRef.current) setReport(result);
    } finally {
      // SOLO la sonda vigente puede apagar el spinner y soltar el guard. Si una
      // sonda obsoleta lo hiciera, dejaría running=false con una sonda viva y el
      // panel quedaría abierto y VACÍO para siempre; además habilitaría dos
      // sondas en paralelo si el médico cierra y abre el panel.
      if (gen === genRef.current) {
        runningRef.current = false;
        setRunning(false);
      }
    }
  }, []);

  // Cada intento de login nuevo invalida el veredicto anterior. Sin esto el
  // panel se quedaba PEGADO: el intento 1 decía OK, y en el intento 2 —con la
  // red ya caída— seguía mostrando "Conexión con el servidor: OK".
  //
  // Y ojo: acá hay que DISPARAR la sonda, no solo limpiarla. Delegarlo al efecto
  // de apertura no funciona, porque ese efecto depende de que `hasReport` pase
  // de true a false — y si el attempt llega con una sonda EN VUELO, report ya
  // era null, ninguna dep cambia, el efecto no vuelve a correr y el panel queda
  // abierto y vacío de forma permanente.
  const lastAttempt = useRef(attempt);
  useEffect(() => {
    if (attempt !== lastAttempt.current) {
      lastAttempt.current = attempt;
      setCopied(false);
      if (open) {
        runCheck(true);
      } else {
        // Cerrado: basta con invalidar lo viejo. Cuando se abra, el efecto de
        // apertura sondea de cero.
        genRef.current += 1;
        runningRef.current = false;
        setReport(null);
      }
    }
  }, [attempt, open, runCheck]);

  // El panel se abre solo cuando el login falla por red. Sin esto, el Collapse
  // abría VACÍO (report=null, running=false → no renderiza nada) y el primer
  // clic en "¿Problemas para entrar?" lo cerraba: la usuaria necesitaba dos
  // clics para ver el diagnóstico, justo en el caso para el que se hizo.
  const hasReport = report !== null;
  useEffect(() => {
    if (open && !hasReport && !runningRef.current) {
      runCheck();
    }
  }, [open, hasReport, runCheck]);

  // El panel se abre SOLO, y en un celular nace fuera de la pantalla: medido en
  // un iPhone 14 (viewport 390x664) el veredicto aparecía en `top: 711` y la
  // lista de dominios para informática en `852`, con la página sin scrollear.
  // O sea que el médico veía el mensaje rojo y la salida quedaba invisible más
  // abajo — justo en el aparato desde el que entra cuando su red lo bloquea.
  // `nearest` scrollea lo mínimo para que entre, sin saltar de más.
  const panelRef = useRef(null);
  useEffect(() => {
    if (!open || !hasReport) return;
    try {
      panelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } catch {
      /* si el navegador no lo soporta, el panel igual está: no se rompe nada */
    }
  }, [open, hasReport]);

  const handleToggle = () => {
    // Solo abre/cierra: la sonda la dispara el efecto de arriba, así el camino
    // "clic manual" y el camino "se abrió solo" pasan por el mismo lugar.
    onToggle(!open);
  };

  const copyDetail = async () => {
    try {
      await navigator.clipboard.writeText(`${report.title}\n\n${report.detail}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      <Button
        type="button"
        variant="text"
        onClick={handleToggle}
        startIcon={<HelpOutlineIcon fontSize="small" />}
        sx={linkBtnSx}
      >
        ¿Problemas para entrar?
      </Button>

      <Collapse in={open} unmountOnExit>
        {running && (
          <Typography
            sx={{
              mt: 1,
              fontSize: 12.5,
              color: "text.muted",
              textAlign: "left",
            }}
          >
            Revisando la conexión con el servidor...
          </Typography>
        )}

        {report && (
          <Box ref={panelRef} sx={panelSx(report.ok)}>
            <Typography
              sx={{
                fontSize: 13.5,
                fontWeight: 700,
                color: report.ok ? "success.main" : "error.main",
              }}
            >
              {report.title}
            </Typography>
            <Typography
              sx={{
                mt: 0.75,
                fontSize: 12.5,
                lineHeight: 1.55,
                color: "text.secondary",
              }}
            >
              {hintFor(report, loginState)}
            </Typography>

            <Typography
              sx={{ mt: 1.25, fontSize: 11, fontWeight: 600, color: "text.faint" }}
            >
              Detalle técnico para soporte
            </Typography>
            <Box sx={detailSx}>{report.detail}</Box>

            <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
              <Button
                type="button"
                size="small"
                variant="text"
                onClick={copyDetail}
                startIcon={<ContentCopyIcon fontSize="small" />}
                sx={{ ...linkBtnSx, fontSize: 12 }}
              >
                {copied ? "Copiado" : "Copiar detalle"}
              </Button>
              <Button
                type="button"
                size="small"
                variant="text"
                onClick={runCheck}
                disabled={running}
                sx={{ ...linkBtnSx, fontSize: 12 }}
              >
                Probar de nuevo
              </Button>
            </Stack>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default ConnectionDiagnostics;
